import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { createSiteContent, getSiteContent, updateSiteContentId } from '../../services/api';

const saveButtonStyle = {
  bgcolor: '#7BAAA7',
  '&:hover': { bgcolor: '#6A9391' },
  color: '#000',
  textTransform: 'none',
  fontWeight: 600,
};

const DEFAULT_HISTORY = `Bem-vindo ao Laboratório e Centro de Documentação e Memória da Cultura na Amazônia (CEDOMCA), o acervo da Faculdade de Artes da Universidade Federal do Amazonas (UFAM). Nós somos uma equipe dedicada de professores e estudantes que trabalham juntos para preservar e compartilhar a rica história da arte na região amazônica.

O Laboratório e Centro de Documentação e Memória da Cultura na Amazônia (CEDOMCA) foi criado em 23 de janeiro de 2018, na Faculdade de Artes da Universidade Federal do Amazonas, tendo como finalidades: 1) a salvaguarda da memória da cultura na Amazônia, presentificada em “documentos” que compõem o seu acervo; 2) a acessibilidade do seu acervo para o público vasto - pesquisadores, estudantes e público em geral e; 3) o fomento do ensino, de pesquisas, ações de extensão e exposições a partir de seu acervo, além da colaboração nas demais exposições e ações cujo acervo possui afinidades.

O CEDOMCA tem parceria multidisciplinar com as Ciências da Informação, com os procedimentos da Arquivologia, com a Museologia e com a Biblioteconomia. Estes campos contribuem para as ações de trabalho mais técnico de tratamento e guarda de documentos.

O acervo do CEDOMCA é constituído, a princípio, por um conjunto de livros, bibliografias, LPs oriundos do extinto Conservatório Musical “Joaquim Franco”(1965-1978); dos livros pertencentes a biblioteca particular da artista Bernadete Andrade (1953-2007); cópias de partituras de Arnaldo Rebello (1905-1987) e Lindalva Cruz (1908-2005); dissertações e teses temáticas da Cultura Amazônica; coleção de LPs temáticos da cultura dos povos tradicionais do Brasil; gravações em áudio de cantos, danças e rituais dos povos da floresta, entre outros materiais.`;

const DEFAULT_ACERVO = [
  {
    category: "Música",
    period: "1900-1980",
    title: "Partituras Históricas",
    description: "Coleção de partituras originais do século XIX e XX, incluindo composições regionais.",
    quantity: "230 peças",
  },
  {
    category: "Áudio",
    period: "1930-2000",
    title: "Documentos Sonoros",
    description: "Gravações históricas em diversos formatos: fitas cassete, discos de vinil e CDs.",
    quantity: "450 registros",
  },
  {
    category: "Visual",
    period: "1920-1995",
    title: "Fotografias",
    description: "Acervo fotográfico de eventos culturais, artistas e patrimônio histórico de Araraquara.",
    quantity: "1.200 fotografias",
  },
  {
    category: "Arquivo",
    period: "1930-1979",
    title: "Conservatório Musical Joaquim Franco",
    description: "Documentação completa do Conservatório Musical (1930-1979), incluindo programas, certificados e registros administrativos.",
    quantity: "500+ documentos",
  },
  {
    category: "Bibliografia",
    period: "1950-2020",
    title: "Biblioteca Especializada",
    description: "Livros e periódicos sobre música, cultura e história regional de Araraquara.",
    quantity: "800 volumes",
  },
  {
    category: "Vídeo",
    period: "1970-2010",
    title: "Material Audiovisual",
    description: "Vídeos de apresentações, entrevistas com artistas e documentários sobre cultura regional.",
    quantity: "150 registros",
  },
];

const DEFAULT_YOUTUBE_LINK = 'https://youtube.com/playlist?list=PLpPGbI2fk_PYfrUhmX_KjVsC6EfE02ZUx&si=WK0JAhftraNGicd8';
const DEFAULT_YOUTUBE_TEXT = 'Vídeos sobre o acervo, entrevistas e conteúdos sobre cultura e memória amazônica.';
const DEFAULT_ACERVO_TITLE = 'Acervo de Artes CEDOMCA';
const DEFAULT_YOUTUBE_TITLE = 'Playlist CEDOMCA';
const DEFAULT_ACERVO_GRID_TITLE = 'Explore o Acervo';

export default function EditAcervoPage() {
  const navigate = useNavigate();

  const [contentId, setContentId] = useState(null);
  const [history, setHistory] = useState('');
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [acervo, setAcervo] = useState([]);
  const [location, setLocation] = useState({ address: '', locationName: '', mapsLink: '', institutionalEmail: '' });
  const [youtubeLink, setYoutubeLink] = useState('');
  const [youtubeText, setYoutubeText] = useState('');
  const [acervoTitle, setAcervoTitle] = useState('');
  const [youtubeTitle, setYoutubeTitle] = useState('');
  const [acervoGridTitle, setAcervoGridTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    document.title = 'CEDOMCA | Editar Página do Acervo';
    (async () => {
      try {
        const { data } = await getSiteContent();
        if (data.length > 0) {
          const item = data[0];
          setContentId(item.id);
          setHistory(item.history || DEFAULT_HISTORY);
          setProjects(item.projects || []);
          setContacts(item.contacts || []);
          setAcervo(item.acervo && item.acervo.length > 0 ? item.acervo : DEFAULT_ACERVO);
          setLocation({
            address: item.address || '',
            locationName: item.locationName || '',
            mapsLink: item.mapsLink || '',
            institutionalEmail: item.institutionalEmail || '',
          });
          setYoutubeLink(item.youtubeLink || DEFAULT_YOUTUBE_LINK);
          setYoutubeText(item.youtubeText || DEFAULT_YOUTUBE_TEXT);
          setAcervoTitle(item.acervoTitle || DEFAULT_ACERVO_TITLE);
          setYoutubeTitle(item.youtubeTitle || DEFAULT_YOUTUBE_TITLE);
          setAcervoGridTitle(item.acervoGridTitle || DEFAULT_ACERVO_GRID_TITLE);
        } else {
          setHistory(DEFAULT_HISTORY);
          setAcervo(DEFAULT_ACERVO);
          setYoutubeLink(DEFAULT_YOUTUBE_LINK);
          setYoutubeText(DEFAULT_YOUTUBE_TEXT);
          setAcervoTitle(DEFAULT_ACERVO_TITLE);
          setYoutubeTitle(DEFAULT_YOUTUBE_TITLE);
          setAcervoGridTitle(DEFAULT_ACERVO_GRID_TITLE);
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleAddAcervoItem = () =>
    setAcervo((prev) => [...prev, { category: '', period: '', title: '', description: '', quantity: '' }]);

  const handleDeleteAcervoItem = (index) =>
    setAcervo((prev) => prev.filter((_, i) => i !== index));

  const handleChange = (index, field, value) =>
    setAcervo((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        history: history.trim(),
        projects: projects.map(({ title, description, link }) => ({
          title,
          description,
          ...(link ? { link } : {}),
        })),
        contacts: contacts.map(({ name, email, phone, role }) => ({
          name,
          ...(email ? { email } : {}),
          ...(phone ? { phone } : {}),
          ...(role ? { role } : {}),
        })),
        acervo: acervo
          .filter((item) => item.title.trim() && item.description.trim())
          .map(({ category, period, title, description, quantity }) => ({
            category: category.trim(),
            period: period.trim(),
            title: title.trim(),
            description: description.trim(),
            quantity: quantity.trim(),
          })),
        ...(location.address ? { address: location.address } : {}),
        ...(location.locationName ? { locationName: location.locationName } : {}),
        ...(location.mapsLink ? { mapsLink: location.mapsLink } : {}),
        ...(location.institutionalEmail ? { institutionalEmail: location.institutionalEmail } : {}),
        youtubeLink: youtubeLink.trim(),
        youtubeText: youtubeText.trim(),
        acervoTitle: acervoTitle.trim(),
        youtubeTitle: youtubeTitle.trim(),
        acervoGridTitle: acervoGridTitle.trim(),
      };

      if (contentId) {
        await updateSiteContentId(contentId, payload);
      } else {
        const { data } = await createSiteContent(payload);
        setContentId(data.id);
      }
      setSnackbar({ open: true, message: 'Seção do Acervo salva com sucesso!', severity: 'success' });
    } catch (err) {
      const raw = err?.response?.data?.message;
      const detail = Array.isArray(raw) ? raw[0] : (raw ?? 'Erro ao salvar seção do acervo.');
      setSnackbar({ open: true, message: detail, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ minHeight: '100vh', bgcolor: '#f0f0f0' }} />;

  return (
    <Box sx={{ bgcolor: '#f0f0f0', minHeight: '100vh', p: '1.5rem' }}>
      {/* ─── Link de voltar ─── */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/site-content')}
        sx={{ textTransform: 'none', color: 'text.secondary', mb: 2, pl: 0, '&:hover': { bgcolor: 'transparent', color: 'text.primary' } }}
      >
        Voltar para seleção de seções
      </Button>

      {/* ─── Cabeçalho ─── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Editar Página Acervo</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Atualize o texto de apresentação e a história, além dos itens em destaque na grade do acervo
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={saveButtonStyle}
        >
          Salvar Alterações
        </Button>
      </Stack>

      {/* ─── Seção História / Texto de Apresentação ─── */}
      <Paper sx={{ border: '1px solid #e0e0e0', boxShadow: 'none', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
          <HistoryEduOutlinedIcon sx={{ color: '#1565c0' }} />
          <Typography variant="subtitle1" fontWeight={700}>Apresentação e História do Acervo</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
          Atualize o título principal e os parágrafos de história e apresentação institucional. Use parágrafos simples ou pule linhas para criar parágrafos separados na página pública.
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5, mt: 1.5 }}>
          Título Principal da Página
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Ex: Acervo de Artes CEDOMCA"
          value={acervoTitle}
          onChange={(e) => setAcervoTitle(e.target.value)}
          sx={{ mb: 2.5 }}
        />
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
          Texto de Apresentação / História
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={8}
          placeholder="Descreva a história e apresentação institucional do acervo..."
          value={history}
          onChange={(e) => setHistory(e.target.value)}
          inputProps={{ style: { resize: 'vertical' } }}
        />
      </Paper>

      {/* ─── Seção YouTube ─── */}
      <Paper sx={{ border: '1px solid #e0e0e0', boxShadow: 'none', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
          <HistoryEduOutlinedIcon sx={{ color: '#d32f2f' }} />
          <Typography variant="subtitle1" fontWeight={700}>Destaque do YouTube (Playlist / Vídeo)</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1.5 }}>
          Atualize o título de destaque, o link da playlist do YouTube e a descrição que aparecem na página inicial.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Título do YouTube</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Ex: Playlist CEDOMCA"
              value={youtubeTitle}
              onChange={(e) => setYoutubeTitle(e.target.value)}
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Link do YouTube</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="https://youtube.com/..."
              value={youtubeLink}
              onChange={(e) => setYoutubeLink(e.target.value)}
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Descrição do YouTube</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Descreva brevemente a playlist ou vídeo..."
              value={youtubeText}
              onChange={(e) => setYoutubeText(e.target.value)}
              sx={{ mt: 0.5 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ─── Seção Itens do Acervo ─── */}
      <Paper sx={{ border: '1px solid #e0e0e0', boxShadow: 'none', borderRadius: 2, p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <GridViewOutlinedIcon sx={{ color: '#1565c0' }} />
            <Typography variant="subtitle1" fontWeight={700}>Explore o Acervo (Itens em Destaque)</Typography>
          </Stack>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={handleAddAcervoItem}
            sx={saveButtonStyle}
          >
            Adicionar Item
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
          Título da Seção de Exploração do Acervo
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Ex: Explore o Acervo"
          value={acervoGridTitle}
          onChange={(e) => setAcervoGridTitle(e.target.value)}
          sx={{ mb: 3 }}
        />

        {acervo.length === 0 ? (
          <Typography variant="body2" color="text.disabled" fontStyle="italic" align="center" sx={{ py: 3 }}>
            Nenhum item do acervo cadastrado. Clique em &quot;Adicionar Item&quot; para começar.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {acervo.map((item, index) => (
              <Paper key={index} sx={{ border: '1px solid #e0e0e0', boxShadow: 'none', borderRadius: 2, p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                    Item {index + 1}
                  </Typography>
                  <IconButton size="small" color="error" onClick={() => handleDeleteAcervoItem(index)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Categoria</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Ex: Música, Áudio, Visual, Arquivo, Vídeo"
                      value={item.category}
                      onChange={(e) => handleChange(index, 'category', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Período Histórico</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Ex: 1930-2000, Século XIX"
                      value={item.period}
                      onChange={(e) => handleChange(index, 'period', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Título</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Ex: Documentos Sonoros"
                      value={item.title}
                      onChange={(e) => handleChange(index, 'title', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Quantidade / Volume</Typography>
                    <TextField
                      fullWidth size="small" placeholder="Ex: 450 registros, 1.200 fotos"
                      value={item.quantity}
                      onChange={(e) => handleChange(index, 'quantity', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Descrição</Typography>
                    <TextField
                      fullWidth size="small" multiline rows={2}
                      placeholder="Breve descrição sobre o conteúdo deste item no acervo..."
                      value={item.description}
                      onChange={(e) => handleChange(index, 'description', e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      {/* ─── Pré-visualização ─── */}
      <Paper sx={{ border: '1px solid #90caf9', boxShadow: 'none', borderRadius: 2, p: 2.5, mt: 3, bgcolor: '#e3f2fd' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
          <VisibilityOutlinedIcon sx={{ color: '#1565c0' }} />
          <Typography variant="subtitle1" fontWeight={700} color="#1565c0">Pré-visualização</Typography>
          <Typography variant="caption" color="#1976d2">(atualiza em tempo real)</Typography>
        </Stack>

        {/* História */}
        {history.trim() && (
          <Box sx={{ mb: 4, p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #bbdefb' }}>
            <Typography variant="subtitle2" fontWeight={700} color="#1565c0" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Apresentação Institucional
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#111' }}>{acervoTitle || 'Acervo de Artes CEDOMCA'}</Typography>
            {history.split('\n').filter((p) => p.trim()).map((para, i) => (
              <Typography key={i} variant="body2" sx={{ mb: 1.5, color: '#444', lineHeight: 1.6, textIndent: '1rem' }}>
                {para}
              </Typography>
            ))}
          </Box>
        )}

        {/* YouTube Playlist Preview */}
        {(youtubeLink.trim() || youtubeText.trim()) && (
          <Box sx={{ mb: 4, p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid #bbdefb', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ bgcolor: '#d32f2f', color: '#fff', p: 1, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#d32f2f">
                {youtubeTitle || 'Playlist CEDOMCA'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#444', mt: 0.5 }}>
                {youtubeText || 'Sem descrição'}
              </Typography>
              {youtubeLink && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, wordBreak: 'break-all' }}>
                  Link: {youtubeLink}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Explore o Acervo */}
        {acervo.filter((item) => item.title.trim()).length > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight={700} color="#1565c0" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {acervoGridTitle || 'Explore o Acervo'}
            </Typography>
            <Grid container spacing={2}>
              {acervo.filter((item) => item.title.trim()).map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper sx={{ p: 2, border: '1px solid #bbdefb', boxShadow: 'none', borderRadius: 2, bgcolor: '#fff', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Box sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          fontSize: 10,
                          fontWeight: 'bold',
                          bgcolor: '#e0f2f1',
                          color: '#004d40',
                          textTransform: 'uppercase'
                        }}>
                          {item.category || 'Geral'}
                        </Box>
                        {item.period && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                            {item.period}
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="body2" fontWeight={700} sx={{ mb: 1, color: '#222' }}>{item.title}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, lineHeight: 1.4 }}>
                        {item.description}
                      </Typography>
                    </Box>
                    {item.quantity && (
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#333', alignSelf: 'flex-start' }}>
                        {item.quantity}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Estado vazio */}
        {!history.trim() && acervo.filter((item) => item.title.trim()).length === 0 && (
          <Typography variant="body2" color="#1976d2" fontStyle="italic" align="center" sx={{ py: 2 }}>
            Preencha os campos acima para ver a pré-visualização.
          </Typography>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert elevation={6} variant="filled" severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
